import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Stack,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateDrawerHeaderFlushSx,
  harmonateDialogCloseIconSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
} from '../../../theme/harmonateStyles';
import { useUserRights } from '../../../context/UserRightsContext';
import { hasWorkflowProductPermission } from './workflowProductScope';

/** Conductor file-instance comment payloads use snake_case. */
export const COMMENT_MAX_LENGTH = 250;

const MAX_COMMENT_THREAD = 50;

/** One-line snippet for confirm dialog (mapping-style quoted label). */
function commentLabelForConfirmDelete(raw) {
  if (raw == null || !String(raw).trim()) return null;
  let t = String(raw).replace(/\s+/g, ' ').trim();
  const max = 140;
  if (t.length > max) return `${t.slice(0, max)}…`;
  return t;
}

const backendBase = () => (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003/').replace(/\/+$/, '/');

export function tenantUserDisplayName(u) {
  if (!u) return '';
  const name = `${(u.first_name || '').trim()} ${(u.last_name || '').trim()}`.trim();
  return name || u.username || u.email || '';
}

/** Display timestamp in the user’s locale (local time). */
export function formatCommentTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return String(iso);
  }
}

function taggedUserIdsFromComment(c) {
  const raw = c?.tagged_user_ids ?? c?.taggedUserIds;
  return Array.isArray(raw) ? raw.filter(Boolean) : [];
}

/**
 * Parse @DisplayName tokens in comment text (longest label first).
 * Labels come from userLabel (typically full name, else username/email).
 */
export function extractTaggedUserIdsFromCommentText(comment, tenantUsers, userLabelFn) {
  if (!comment || !tenantUsers?.length) return [];
  const rows = tenantUsers
    .map((u) => ({ id: u.id, label: userLabelFn(u) }))
    .filter((r) => r.label && String(r.label).trim());
  rows.sort((a, b) => b.label.length - a.label.length);
  const ids = new Set();
  let i = 0;
  const text = String(comment);
  while (i < text.length) {
    const at = text.indexOf('@', i);
    if (at === -1) break;
    const tail = text.slice(at + 1);
    let matchedLen = 0;
    let matchedId = null;
    for (const { id, label } of rows) {
      if (!tail.startsWith(label)) continue;
      const boundary = tail[label.length];
      if (boundary === undefined || /[\s,.;:!?)\]}]/.test(boundary)) {
        matchedId = id;
        matchedLen = label.length;
        break;
      }
    }
    if (matchedId) {
      ids.add(matchedId);
      i = at + 1 + matchedLen;
    } else {
      i = at + 1;
    }
  }
  return [...ids];
}

function MentionCommentTextField({
  value,
  onChange,
  disabled,
  maxLength,
  placeholder,
  helperText,
  tenantUsers,
  userLabel,
  usersLoading,
  size = 'small',
}) {
  const inputRef = useRef(null);
  const mentionListRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [mention, setMention] = useState(null);
  const [hl, setHl] = useState(0);

  const closeMention = useCallback(() => {
    setMention(null);
    setHl(0);
  }, []);

  const analyzeMention = useCallback((text, selStart) => {
    const textBefore = text.slice(0, selStart);
    const lastAt = textBefore.lastIndexOf('@');
    if (lastAt < 0) {
      closeMention();
      return;
    }
    const afterAt = textBefore.slice(lastAt + 1);
    if (afterAt.includes('\n')) {
      closeMention();
      return;
    }
    setMention({ atIndex: lastAt, queryEnd: selStart });
    setHl(0);
  }, [closeMention]);

  const query = mention ? value.slice(mention.atIndex + 1, mention.queryEnd) : '';

  const filtered = useMemo(() => {
    if (!mention) return [];
    const q = query.toLowerCase();
    const list = tenantUsers.filter((u) => {
      const lab = userLabel(u);
      return lab && lab.toLowerCase().includes(q);
    });
    list.sort((a, b) => {
      const la = userLabel(a).toLowerCase();
      const lb = userLabel(b).toLowerCase();
      const pa = la.startsWith(q) ? 0 : 1;
      const pb = lb.startsWith(q) ? 0 : 1;
      if (pa !== pb) return pa - pb;
      return la.localeCompare(lb);
    });
    return list.slice(0, 14);
  }, [mention, query, tenantUsers, userLabel]);

  useEffect(() => {
    if (!mention) return;
    if (!filtered.length) {
      setHl(0);
      return;
    }
    setHl((i) => Math.min(i, filtered.length - 1));
  }, [mention, filtered.length, query]);

  const insertMention = useCallback(
    (u) => {
      const inp = inputRef.current;
      if (!mention || !inp) return;
      const cur = inp.value.slice(0, maxLength);
      const { atIndex, queryEnd } = mention;
      const label = userLabel(u);
      if (!label) return;
      const before = cur.slice(0, atIndex);
      const after = cur.slice(queryEnd);
      const insertion = `@${label} `;
      const merged = `${before}${insertion}${after}`.slice(0, maxLength);
      onChange(merged);
      closeMention();
      const pos = Math.min(before.length + insertion.length, merged.length);
      requestAnimationFrame(() => {
        inp.focus();
        inp.setSelectionRange(pos, pos);
      });
    },
    [mention, maxLength, onChange, closeMention, userLabel],
  );

  const handleChange = (e) => {
    const next = e.target.value.slice(0, maxLength);
    const sel = e.target.selectionStart ?? next.length;
    onChange(next);
    analyzeMention(next, sel);
  };

  const handleSelect = (e) => {
    const t = e.target;
    analyzeMention(t.value, t.selectionStart ?? t.value.length);
  };

  const handleKeyDown = (e) => {
    if (!mention) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      closeMention();
      return;
    }
    if (!filtered.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHl((x) => Math.min(x + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHl((x) => Math.max(x - 1, 0));
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      insertMention(filtered[hl]);
    }
  };

  const listOpen = Boolean(mention && anchorEl && (usersLoading || tenantUsers.length > 0));

  useEffect(() => {
    if (!mention) return undefined;
    const onDocMouseDown = (e) => {
      const t = e.target;
      if (mentionListRef.current?.contains(t)) return;
      if (inputRef.current?.contains(t)) return;
      closeMention();
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    return () => document.removeEventListener('mousedown', onDocMouseDown, true);
  }, [mention, closeMention]);

  return (
      <Box ref={setAnchorEl} sx={{ position: 'relative', width: '100%' }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          minRows={2}
          size={size}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          onChange={handleChange}
          onSelect={handleSelect}
          onKeyDown={handleKeyDown}
          inputProps={{ maxLength }}
          helperText={helperText}
          sx={{ bgcolor: 'background.paper' }}
        />
        <Popper
          open={listOpen}
          anchorEl={anchorEl}
          placement="bottom-start"
          modifiers={[
            { name: 'offset', options: { offset: [0, 4] } },
            { name: 'preventOverflow', options: { padding: 8 } },
          ]}
          sx={{
            width: anchorEl?.offsetWidth ?? '100%',
            zIndex: (theme) => theme.zIndex.drawer + 2,
          }}
        >
          <Paper
            ref={mentionListRef}
            variant="outlined"
            sx={{ maxHeight: 260, overflow: 'auto', boxShadow: 2 }}
            onMouseDown={(e) => e.preventDefault()}
          >
            {usersLoading ? (
              <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={22} />
              </Box>
            ) : filtered.length === 0 ? (
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  No matching people
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding>
                {filtered.map((u, idx) => (
                  <ListItem key={u.id} disablePadding>
                    <ListItemButton selected={idx === hl} onClick={() => insertMention(u)}>
                      <ListItemText primary={userLabel(u) || u.id} secondary={u.email || u.username || null} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Popper>
      </Box>
  );
}

async function fetchTenantUsers() {
  const token = localStorage.getItem('token');
  const tenantId = localStorage.getItem('tenant_id');
  if (!token || !tenantId) throw new Error('Missing tenant or authentication');
  const url = `${backendBase()}conductor/tenant/${encodeURIComponent(tenantId)}/all-users`;
  const resp = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  if (!resp.ok) throw new Error(`Failed to load users (${resp.status})`);
  const data = await resp.json();
  return Array.isArray(data) ? data : [];
}

/**
 * Drawer: global document comments (not step-scoped).
 * Props:
 * - open / onClose
 * - fileInstanceId
 * - notifyParentSynced — optional async (e.g. refetch file instance in parent so header stays in sync)
 */
const DocumentCommentsDrawer = ({
  open,
  onClose,
  fileInstanceId,
  notifyParentSynced,
  productScope,
}) => {
  const { hasPermission, isHarmonateUser } = useUserRights();
  const canAdd = hasWorkflowProductPermission(hasPermission, isHarmonateUser, productScope, 'add');
  const canEdit = hasWorkflowProductPermission(hasPermission, isHarmonateUser, productScope, 'edit');
  const canDelete = hasWorkflowProductPermission(hasPermission, isHarmonateUser, productScope, 'delete');
  const currentUserId = localStorage.getItem('user_id') || '';

  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [comments, setComments] = useState([]);
  const [listError, setListError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [newBody, setNewBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuComment, setMenuComment] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const userLabel = useCallback((u) => tenantUserDisplayName(u) || u?.id?.slice?.(0, 8), []);

  const refreshComments = useCallback(async () => {
    if (!fileInstanceId) return [];
    const token = localStorage.getItem('token');
    if (!token) {
      setListError('Authentication required');
      return [];
    }
    setLoading(true);
    setListError(null);
    try {
      const url = `${backendBase()}conductor/file-instances/${encodeURIComponent(fileInstanceId)}`;
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!resp.ok) throw new Error(`Failed to load comments (${resp.status})`);
      const fi = await resp.json();
      const raw = fi?.comments ?? fi?.Comments ?? [];
      const normalized = [...raw].sort((a, b) => {
        const ta = new Date(a.commented_at || 0).getTime();
        const tb = new Date(b.commented_at || 0).getTime();
        return tb - ta;
      });
      setComments(normalized);
      return normalized;
    } catch (e) {
      setListError(e.message || 'Failed to load comments');
      return [];
    } finally {
      setLoading(false);
    }
  }, [fileInstanceId]);

  useEffect(() => {
    if (!open || !fileInstanceId) return;
    refreshComments();
  }, [open, fileInstanceId, refreshComments]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setUsersLoading(true);
      setUsersError(null);
      try {
        const list = await fetchTenantUsers();
        if (!cancelled) setTenantUsers(list);
      } catch (e) {
        if (!cancelled) setUsersError(e.message || 'Could not load user list');
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const resolveAuthorLabel = useCallback(
    (userId) => {
      const u = tenantUsers.find((x) => x.id === userId);
      if (u) return userLabel(u);
      return userId ? `User ${String(userId).slice(0, 8)}…` : 'Unknown user';
    },
    [tenantUsers, userLabel],
  );

  const syncParent = useCallback(async () => {
    if (typeof notifyParentSynced === 'function') {
      try {
        await notifyParentSynced();
      } catch {
        /* parent handles errors */
      }
    }
  }, [notifyParentSynced]);

  const handleSubmitNew = async () => {
    if (!canAdd) return;
    if (!fileInstanceId || !currentUserId) {
      setSubmitError('Missing document or signed-in user');
      return;
    }
    const trimmed = newBody.trim();
    if (!trimmed) {
      setSubmitError('Enter a comment');
      return;
    }
    if (trimmed.length > COMMENT_MAX_LENGTH) {
      setSubmitError(`Comment must be ${COMMENT_MAX_LENGTH} characters or fewer`);
      return;
    }
    if (comments.length >= MAX_COMMENT_THREAD) {
      setSubmitError(`Maximum ${MAX_COMMENT_THREAD} comments per document`);
      return;
    }
    const tagged_user_ids = extractTaggedUserIdsFromCommentText(trimmed, tenantUsers, userLabel);
    setSubmitting(true);
    setSubmitError(null);
    try {
      const token = localStorage.getItem('token');
      const url = `${backendBase()}conductor/file-instances/${encodeURIComponent(fileInstanceId)}/comments`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: currentUserId,
          comment: trimmed,
          tagged_user_ids,
        }),
      });
      if (!resp.ok) {
        const errText = await resp.text();
        throw new Error(errText || `Save failed (${resp.status})`);
      }
      setNewBody('');
      await refreshComments();
      await syncParent();
    } catch (e) {
      setSubmitError(e.message || 'Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenMenu = (e, c) => {
    setMenuAnchorEl(e.currentTarget);
    setMenuComment(c);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuComment(null);
  };

  const startEdit = (c) => {
    if (!canEdit) return;
    setEditingId(c.id);
    let body = c.comment || '';
    const ids = taggedUserIdsFromComment(c);
    const extra = [];
    for (const id of ids) {
      const u = tenantUsers.find((t) => t.id === id) || { id };
      const lab = userLabel(u);
      if (!lab) continue;
      const token = `@${lab}`;
      if (!body.includes(token)) {
        extra.push(token);
      }
    }
    if (extra.length) {
      body = `${body.trimEnd()} ${extra.join(' ')}`.trim();
    }
    setEditBody(body.slice(0, COMMENT_MAX_LENGTH));
    handleCloseMenu();
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditBody('');
  };

  const saveEdit = async () => {
    if (!canEdit) return;
    if (!fileInstanceId || !editingId) return;
    const trimmed = editBody.trim();
    if (!trimmed) {
      setSubmitError('Comment cannot be empty');
      return;
    }
    if (trimmed.length > COMMENT_MAX_LENGTH) {
      setSubmitError(`Comment must be ${COMMENT_MAX_LENGTH} characters or fewer`);
      return;
    }
    setEditSaving(true);
    setSubmitError(null);
    try {
      const token = localStorage.getItem('token');
      const tagged_user_ids = extractTaggedUserIdsFromCommentText(trimmed, tenantUsers, userLabel);
      const url = `${backendBase()}conductor/file-instances/${encodeURIComponent(fileInstanceId)}/comments/${encodeURIComponent(editingId)}`;
      const resp = await fetch(url, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment: trimmed, tagged_user_ids }),
      });
      if (!resp.ok) {
        const errText = await resp.text();
        throw new Error(errText || `Update failed (${resp.status})`);
      }
      cancelEdit();
      await refreshComments();
      await syncParent();
    } catch (e) {
      setSubmitError(e.message || 'Failed to update comment');
    } finally {
      setEditSaving(false);
    }
  };

  const openDeleteDialog = (c) => {
    if (!canDelete) return;
    if (!c?.id) return;
    setCommentToDelete(c);
    setDeleteDialogOpen(true);
    handleCloseMenu();
  };

  const cancelDeleteDialog = () => {
    if (deleteSubmitting) return;
    setDeleteDialogOpen(false);
    setCommentToDelete(null);
  };

  const confirmDeleteComment = async () => {
    if (!canDelete) return;
    const c = commentToDelete;
    if (!fileInstanceId || !c?.id) return;
    setDeleteSubmitting(true);
    setSubmitError(null);
    try {
      const token = localStorage.getItem('token');
      const url = `${backendBase()}conductor/file-instances/${encodeURIComponent(fileInstanceId)}/comments/${encodeURIComponent(c.id)}`;
      const resp = await fetch(url, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!resp.ok) {
        const errText = await resp.text();
        throw new Error(errText || `Delete failed (${resp.status})`);
      }
      cancelDeleteDialog();
      await refreshComments();
      await syncParent();
    } catch (e) {
      setSubmitError(e.message || 'Failed to delete comment');
      cancelDeleteDialog();
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const canMutate = (c) =>
    (canEdit || canDelete) && String(c?.user_id) === String(currentUserId) && !!currentUserId;

  return (
    <>
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 420 },
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={harmonateDrawerHeaderFlushSx}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ChatBubbleOutlineOutlinedIcon sx={{ color: ht.white, fontSize: 'small' }} />
          <Typography variant="subtitle1" fontWeight={600} sx={{ color: ht.white }}>
            Comments
          </Typography>
          {comments.length > 0 && (
            <Typography variant="caption" sx={{ color: ht.white }}>
              ({comments.length})
            </Typography>
          )}
        </Stack>
        <IconButton size="small" onClick={onClose} aria-label="Close comments" sx={harmonateDialogCloseIconSx}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2 }}>
        {usersError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {usersError} — mentions may be limited.
          </Alert>
        )}
        {listError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setListError(null)}>
            {listError}
          </Alert>
        )}
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : comments.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No comments yet. Add one below.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {comments.map((c) => {
              const taggedIds = taggedUserIdsFromComment(c);
              return (
              <Paper key={c.id} variant="outlined" sx={{ p: 1.5, position: 'relative' }}>
                {canMutate(c) && editingId !== c.id && (
                  <IconButton
                    size="small"
                    sx={{ position: 'absolute', top: 4, right: 4 }}
                    aria-label="Comment actions"
                    onClick={(e) => handleOpenMenu(e, c)}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                )}
                {editingId === c.id ? (
                  <Stack spacing={1}>
                    <MentionCommentTextField
                      value={editBody}
                      onChange={(next) => setEditBody(next)}
                      disabled={editSaving}
                      maxLength={COMMENT_MAX_LENGTH}
                      placeholder="Edit your note… Type @ to mention someone"
                      helperText={`${editBody.length}/${COMMENT_MAX_LENGTH} · Type @ to notify someone`}
                      tenantUsers={tenantUsers}
                      userLabel={userLabel}
                      usersLoading={usersLoading}
                    />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" onClick={cancelEdit} disabled={editSaving}>
                        Cancel
                      </Button>
                      <Button size="small" variant="contained" onClick={saveEdit} disabled={editSaving}>
                        {editSaving ? <CircularProgress size={18} /> : 'Save'}
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', pr: canMutate(c) ? 4 : 0 }}
                    >
                      {c.comment}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      {resolveAuthorLabel(c.user_id)} · {formatCommentTime(c.commented_at)}
                    </Typography>
                    {taggedIds.length > 0 && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Tagged: {taggedIds.map((id) => resolveAuthorLabel(id)).join(', ')}
                      </Typography>
                    )}
                  </>
                )}
              </Paper>
            );
            })}
          </Stack>
        )}
      </Box>

      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          New comment (max {COMMENT_MAX_LENGTH} characters).
        </Typography>
        <Box sx={{ mb: 1.5 }}>
          <MentionCommentTextField
            value={newBody}
            onChange={setNewBody}
            disabled={submitting || !canAdd}
            maxLength={COMMENT_MAX_LENGTH}
            placeholder={canAdd ? 'Add a comment...' : 'You do not have permission to add comments'}
            helperText={`${newBody.length}/${COMMENT_MAX_LENGTH}`}
            tenantUsers={tenantUsers}
            userLabel={userLabel}
            usersLoading={usersLoading}
          />
        </Box>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setNewBody('');
              setSubmitError(null);
            }}
            disabled={submitting || !canAdd}
          >
            Clear
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={handleSubmitNew}
            disabled={submitting || !newBody.trim() || !canAdd}
          >
            {submitting ? <CircularProgress size={20} color="inherit" /> : 'Add comment'}
          </Button>
        </Stack>
      </Box>

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleCloseMenu}>
        {canEdit && (
          <MenuItem
            onClick={() => {
              if (menuComment) startEdit(menuComment);
            }}
          >
            Edit
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem
            onClick={() => {
              if (menuComment) openDeleteDialog(menuComment);
            }}
          >
            Delete
          </MenuItem>
        )}
      </Menu>
    </Drawer>

      {/* Delete confirmation — same pattern as GL Sync */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDeleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
        aria-labelledby="comment-delete-dialog-title"
      >
        <DialogTitle
          id="comment-delete-dialog-title"
          sx={harmonateDialogTitleSx}
        >
          <DeleteIcon sx={{ color: ht.red, fontSize: '24px' }} />
          <Typography variant="h6" component="div" sx={{ fontWeight: 700, color: 'inherit' }}>
            Confirm Delete
          </Typography>
        </DialogTitle>

        <DialogContent sx={harmonateDialogContentSx}>
          <Typography variant="body1" component="div">
            {(() => {
              const label = commentLabelForConfirmDelete(commentToDelete?.comment);
              if (label) {
                return (
                  <>
                    Are you sure you want to delete the comment {'\u2018'}
                    {label}
                    {'\u2019'}?
                  </>
                );
              }
              return <>Are you sure you want to delete this comment?</>;
            })()}
          </Typography>
          <Typography variant="body1" sx={{ mt: 1.5 }} component="div">
            This action cannot be undone.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={cancelDeleteDialog}
            disabled={deleteSubmitting}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteComment}
            disabled={deleteSubmitting}
            variant="contained"
            sx={harmonateAbortButtonSx}
          >
            {deleteSubmitting ? <CircularProgress size={22} sx={{ color: ht.white }} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentCommentsDrawer;
